import { useParams } from "@tanstack/react-router";
import { Button, Flex, Select } from "antd";
import { useEffect, useState } from "react";
import { useProjects } from "@/common/artemis-operations/projects";
import type { Project } from "@/common/model";
import { CustomEmpty } from "../base";
import { CustomDrawer } from "../layout/workspace/custom-drawer";

interface ProjectSelectDrawerProps {
	open: boolean;
	onClose: () => void;
	/** Currently linked project IDs. */
	selectedProjectIds: string[];
	onSave: (projectIds: string[]) => void;
}

/**
 * Drawer for editing which projects a resource links to. Editing the whole set here rather than
 * removing links inline keeps the details row to plain links.
 */
export function ProjectSelectDrawer({
	open,
	onClose,
	selectedProjectIds,
	onSave,
}: ProjectSelectDrawerProps) {
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId: string;
	};
	const { data: projects } = useProjects(workspaceId);
	const [selected, setSelected] = useState<string[]>(selectedProjectIds);

	// Reopening after a save elsewhere must show the stored value, not the last edit.
	useEffect(() => {
		if (open) setSelected(selectedProjectIds);
	}, [open, selectedProjectIds]);

	const projectOptions = (projects ?? [])
		.filter((p): p is NonNullable<Project> => !!p)
		.map((p) => ({ value: p.id, label: p.name }));

	const submit = () => {
		onSave(selected);
		onClose();
	};

	return (
		<CustomDrawer
			open={open}
			onClose={onClose}
			title="Projects"
			footer={
				<Flex gap={8} justify="flex-end">
					<Button onClick={onClose}>Cancel</Button>
					<Button type="primary" onClick={submit}>
						Save
					</Button>
				</Flex>
			}
		>
			{projectOptions.length === 0 ? (
				<CustomEmpty description="projects" />
			) : (
				<Select
					mode="multiple"
					style={{ width: "100%" }}
					placeholder="Select projects"
					value={selected}
					options={projectOptions}
					onChange={setSelected}
				/>
			)}
		</CustomDrawer>
	);
}
